import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

interface FAQ {
  id: number;
  category: string;
  question: string;
  answer: string;
}

const Help: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqs: FAQ[] = [
    // Getting Started
    {
      id: 1,
      category: 'Getting Started',
      question: 'How do I create my first course?',
      answer: 'Click on "Create with AI" from your dashboard, choose a niche and topic, and our AI will generate a complete course outline in minutes. You can also start from a template or create manually.'
    },
    {
      id: 2,
      category: 'Getting Started',
      question: 'What\'s the difference between AI-generated and template courses?',
      answer: 'AI-generated courses are created from scratch based on your topic and preferences. Templates are pre-designed course structures you can customize. Both options are powerful - choose based on your needs!'
    },
    {
      id: 3,
      category: 'Getting Started',
      question: 'How many courses can I create on the Free plan?',
      answer: 'Free users can create up to 3 AI-generated courses. You have unlimited access to templates and can create unlimited manual courses. Upgrade to Basic or Premium for unlimited AI-generated courses.'
    },

    // Account & Billing
    {
      id: 4,
      category: 'Account & Billing',
      question: 'How do I upgrade my plan?',
      answer: 'Go to the Pricing page and select your desired tier (Basic or Premium). You\'ll be redirected to a secure Stripe checkout. Your upgrade is instant!'
    },
    {
      id: 5,
      category: 'Account & Billing',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! You can cancel anytime from your dashboard settings. You\'ll retain access until the end of your billing period, then automatically downgrade to Free.'
    },
    {
      id: 6,
      category: 'Account & Billing',
      question: 'Do you offer refunds?',
      answer: 'Since this is a digital product, we don\'t offer refunds. However, we have a generous Free tier that lets you create 3 full AI courses—use it to explore the platform before upgrading!'
    },
    {
      id: 7,
      category: 'Account & Billing',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor, Stripe.'
    },

    // Features
    {
      id: 8,
      category: 'Features',
      question: 'What AI features are included?',
      answer: 'Our AI can generate complete course outlines, lesson content, quizzes, and marketing materials including sales pages, email campaigns, and social media posts.'
    },
    {
      id: 9,
      category: 'Features',
      question: 'Can I edit AI-generated content?',
      answer: 'Absolutely! All AI-generated content is fully editable. Use the AI as a starting point and customize everything to match your voice and style.'
    },
    {
      id: 10,
      category: 'Features',
      question: 'What\'s included in the marketing tools?',
      answer: 'Generate sales page copy, email subject lines, social media posts, and complete marketing campaigns. Premium users get advanced AI generation and analytics.'
    },
    {
      id: 11,
      category: 'Features',
      question: 'Can I export my courses?',
      answer: 'Yes! You can export your courses in multiple formats including PDF, Word, and JSON. Premium users get additional export options.'
    },

    // Technical
    {
      id: 12,
      category: 'Technical',
      question: 'Is my data secure?',
      answer: 'Yes! We use industry-standard encryption (SSL/TLS) for all data transmission. Your courses and personal information are stored securely and never shared with third parties.'
    },
    {
      id: 13,
      category: 'Technical',
      question: 'What browsers are supported?',
      answer: 'Templimize AI works best on Chrome, Firefox, Safari, and Edge (latest versions). We recommend keeping your browser updated for the best experience.'
    },
    {
      id: 14,
      category: 'Technical',
      question: 'Can I access Templimize AI on mobile?',
      answer: 'Yes! Our platform is fully responsive and works on tablets and smartphones. For the best experience, we recommend using a desktop or laptop for course creation.'
    },

    // Troubleshooting
    {
      id: 15,
      category: 'Troubleshooting',
      question: 'AI generation failed - what should I do?',
      answer: 'Try refreshing the page and attempting again. If the issue persists, check your internet connection or try with a different topic. Contact support if problems continue.'
    },
    {
      id: 16,
      category: 'Troubleshooting',
      question: 'My course isn\'t saving - help!',
      answer: 'Ensure you\'re connected to the internet and click the "Save" button. If issues persist, try clearing your browser cache or using a different browser.'
    },
    {
      id: 17,
      category: 'Troubleshooting',
      question: 'I forgot my password. How do I reset it?',
      answer: 'Click "Forgot Password" on the login page. We\'ll send a password reset link to your email. Check your spam folder if you don\'t see it within a few minutes.'
    },

    // Plans & Pricing
    {
      id: 18,
      category: 'Plans & Pricing',
      question: 'What\'s the difference between Basic and Premium?',
      answer: 'Basic includes unlimited AI courses and basic marketing tools. Premium adds custom branding, team collaboration, API access, priority support, and advanced analytics.'
    },
    {
      id: 19,
      category: 'Plans & Pricing',
      question: 'Can I switch between plans?',
      answer: 'Yes! You can upgrade or downgrade anytime. Upgrades are instant. Downgrades take effect at the end of your current billing period.'
    },
    {
      id: 20,
      category: 'Plans & Pricing',
      question: 'Do you offer discounts for students or nonprofits?',
      answer: 'Yes! Contact us at sloane@templimizeai.com with proof of student status or nonprofit registration for special pricing.'
    }
  ];

  const categories = ['all', ...Array.from(new Set(faqs.map(faq => faq.category)))];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div
              onClick={() => navigate('/')}
              className="cursor-pointer"
            >
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                The Course Creator's Vault
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="border-t border-gray-200">
            <nav className="flex overflow-x-auto space-x-4 sm:space-x-6 py-2 px-1">
              <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Dashboard</button>
              <button onClick={() => navigate('/templates')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Templates</button>
              <button onClick={() => navigate('/brand-settings')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Brand</button>
              <button onClick={() => navigate('/pricing')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Pricing</button>
              <button onClick={() => navigate('/help')} className="text-indigo-600 font-semibold whitespace-nowrap text-sm sm:text-base border-b-2 border-indigo-600 pb-2">Help</button>
              <button onClick={() => navigate('/contact')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Contact</button>
            </nav>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">How Can We Help? 🤔</h1>
          <p className="text-xl text-purple-100 mb-8">
            Search our knowledge base or browse by category
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for answers..."
                className="w-full px-6 py-4 pr-12 rounded-full text-gray-900 focus:ring-4 focus:ring-purple-300 focus:outline-none text-lg"
              />
              <svg
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filters */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-600'
                }`}
              >
                {category === 'all' ? 'All Topics' : category}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="max-w-4xl mx-auto">
          {filteredFAQs.length > 0 ? (
            <div className="space-y-4">
              {filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <div className="flex items-start space-x-4 text-left">
                      <span className="text-2xl">❓</span>
                      <div>
                        <span className="text-xs text-purple-600 font-semibold">{faq.category}</span>
                        <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                      </div>
                    </div>
                    <svg
                      className={`w-6 h-6 text-gray-400 transform transition-transform ${
                        expandedFAQ === faq.id ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedFAQ === faq.id && (
                    <div className="px-6 pb-4 pl-16">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600 mb-6">
                Try different keywords or browse all topics
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>

        {/* Still Need Help Section */}
        <div className="mt-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Still need help? 💬</h2>
          <p className="text-purple-100 text-lg mb-6">
            Can't find what you're looking for? We're here to help!
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition"
            >
              Contact Support
            </button>
            <a
              href="mailto:sloane@templimizeai.com"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition"
            >
              Email Us
            </a>
          </div>
          <p className="text-purple-200 text-sm mt-4">
            Average response time: Under 24 hours
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Help;
